import { get, isEmpty } from 'lodash';

import CartAPI from 'api/Cart';
import RecommendationAPI from 'api/Recommendation';
import { fetchPaymentSettings } from 'state/actions/shop';
import ACTIONS, { CART_VIEWS } from 'state/constants';
import CartSelector from 'state/selectors/cart';
import ExperimentsSelector from 'state/selectors/experiments';
import DynamicYield from 'utils/DynamicYield';
import Log from 'utils/Log';
import Rudderstack from 'utils/Rudderstack';
import Settings from 'utils/Settings';

const defaultErrorMessage = 'Something went wrong.';

export const initCart = () => async dispatch => {
  dispatch({ type: ACTIONS.INIT_CART_REQUEST });
  try {
    const cart = await CartAPI.fetchCart();

    dispatch({ type: ACTIONS.INIT_CART_SUCCESS, payload: { cart } });
  } catch (error) {
    Log.error(error, 'Initializing cart failed.');
    dispatch({ type: ACTIONS.INIT_CART_FAILURE, payload: { error } });
  }
};

export const setCartView = view => dispatch =>
  dispatch({ type: ACTIONS.SET_CART_VIEW, payload: view });

export const setCartViewToOverview = () => setCartView(CART_VIEWS.OVERVIEW);

export const setCartViewToGiftMessage = () => setCartView(CART_VIEWS.GIFT_MESSAGE);

export const setCartMessage = message => dispatch =>
  dispatch({ type: ACTIONS.SET_CART_MESSAGE, payload: message });

export const setCartCache = () => dispatch => dispatch({ type: ACTIONS.SET_CART_CACHE });

export const toggleCart =
  (isOpen = true, meta = {}) =>
  dispatch =>
    dispatch({ type: ACTIONS.TOGGLE_CART, payload: isOpen, meta });

export const closeCart = () => toggleCart(false);

export const fetchCartUpsells = cart => async dispatch => {
  if (isEmpty(get(cart, ['entities', 'item'], {}))) {
    return;
  }
  dispatch({ type: ACTIONS.FETCH_CART_UPSELLS_REQUEST });
  try {
    const upsells = await RecommendationAPI.fetchCartRecommendations(cart);
    dispatch({ type: ACTIONS.FETCH_CART_UPSELLS_SUCCESS, payload: { upsells } });
  } catch (error) {
    Log.error(error, 'Fetching cart upsells failed.');
    dispatch({ type: ACTIONS.FETCH_CART_UPSELLS_FAILURE, payload: { error } });
  }
};

export const openCart = meta => async (dispatch, getState) => {
  dispatch(setCartViewToOverview());
  dispatch(toggleCart(true, meta));
  dispatch({ type: ACTIONS.CLOSE_TOP_NAVIGATION, payload: {} });

  const state = getState();
  const cart = get(state, ['ajaxCart', 'data']);
  const origination = get(meta, 'origination');

  if (origination === 'topNavigationCartIcon' && Settings.cartUpsellsV2Enabled) {
    dispatch(fetchCartUpsells(cart));
  }

  if (!isEmpty(get(cart, ['entities', 'item'], {})) && Settings.cartAcceleratedPaymentsEnabled) {
    dispatch(fetchPaymentSettings());
  }

  // Triggers DY custom event and fetches cart experiments only if cart is not empty or coming from atc
  if (
    !isEmpty(get(cart, ['entities', 'item'], {})) ||
    meta === '#add-to-cart' ||
    meta === '#query-param' ||
    meta === '#size-selector'
  ) {
    DynamicYield.cartOpenEvent();
  }
};

export const handleReturnsInsuranceAttributes = () => async (dispatch, getState) => {
  // Fetch latest cart to ensure we have the most up-to-date state
  const cart = await CartAPI.fetchCart();
  const state = getState();
  const wasReturnsInsuranceRendered = CartSelector.getReturnsInsuranceRendered(state);
  const { insuranceItemId, nonInsuranceItemCount } = analyzeCartItems(
    get(cart, ['entities', 'item'], {})
  );

  // Only eligible if no insurance product already in cart AND there are non-final sale items
  const isEligibleForReturnsInsurance =
    !insuranceItemId && nonInsuranceItemCount > 0 ? 'true' : 'false';
  const isExposedToReturnsInsuranceCampaign = wasReturnsInsuranceRendered.toString();

  dispatch({
    type: ACTIONS.UPDATE_CART_ATTRIBUTES_REQUEST,
    payload: {
      _isEligibleForReturnsInsurance: isEligibleForReturnsInsurance,
      _isExposedToReturnsInsuranceCampaign: isExposedToReturnsInsuranceCampaign,
    },
  });

  try {
    await CartAPI.setAttributes({
      _isEligibleForReturnsInsurance: isEligibleForReturnsInsurance,
      _isExposedToReturnsInsuranceCampaign: isExposedToReturnsInsuranceCampaign,
    });
    dispatch({
      type: ACTIONS.UPDATE_CART_ATTRIBUTES_SUCCESS,
      payload: {
        _isEligibleForReturnsInsurance: isEligibleForReturnsInsurance,
        _isExposedToReturnsInsuranceCampaign: isExposedToReturnsInsuranceCampaign,
      },
    });
  } catch (error) {
    Log.error(error, 'Setting returns insurance attributes failed.');
    dispatch({
      type: ACTIONS.UPDATE_CART_ATTRIBUTES_FAILURE,
      payload: { error },
    });
  }
};

const analyzeCartItems = (items = {}) => {
  const insuranceVariantIds = Settings.returnsInsuranceConfig.variantIds;
  const itemIds = Object.keys(items);

  return itemIds.reduce(
    (acc, itemId) => {
      const item = items[itemId];

      if (insuranceVariantIds.includes(itemId)) {
        acc.insuranceItemId = itemId;
      } else if (!item.variant?.isFinalSale) {
        // Only count non-final sale items
        acc.nonInsuranceItemCount++;
      }
      return acc;
    },
    { insuranceItemId: null, nonInsuranceItemCount: 0 }
  );
};

const removeInsuranceIfLastItem = async cart => {
  const { insuranceItemId, nonInsuranceItemCount } = analyzeCartItems(
    get(cart, ['entities', 'item'], {})
  );

  // Remove insurance if there are no eligible items (non-final sale) left
  if (nonInsuranceItemCount === 0 && insuranceItemId) {
    const { cart: updatedCart } = await CartAPI.removeLineItem(insuranceItemId);
    return { cart: updatedCart, removedInsuranceId: insuranceItemId };
  }

  return { cart, removedInsuranceId: null };
};

export const addLineItems = (items, meta, cancelCartOpen) => async (dispatch, getState) => {
  const origination = get(meta, 'origination');
  const targetSelector = get(meta, 'targetSelector');
  const trackingInfo = origination ? { meta: { origination } } : {};
  const lineItemProperties = get(meta, 'lineItemProperties');
  const state = getState();
  const wasCartOpen = cancelCartOpen || CartSelector.getIsCartOpen(state);
  const isAddToCartModalEnabled = ExperimentsSelector.isAddToCartModalEnabled(state);

  let properties = {
    ...lineItemProperties,
  };

  await dispatch({
    type: ACTIONS.ADD_LINE_ITEMS_REQUEST,
    payload: { items, properties },
    ...trackingInfo,
  });

  if (!wasCartOpen && !isAddToCartModalEnabled) {
    dispatch(openCart(targetSelector));
  }

  let cart;
  try {
    const response = await CartAPI.addLineItems({ items, properties });

    cart = response.cart;

    const { insuranceItemId, nonInsuranceItemCount } = analyzeCartItems(
      get(cart, ['entities', 'item'], {})
    );

    if (nonInsuranceItemCount === 0 && insuranceItemId) {
      const { cart: updatedCart } = await CartAPI.removeLineItem(insuranceItemId);
      cart = updatedCart;
      dispatch({
        type: ACTIONS.REMOVE_LINE_ITEM_SUCCESS,
        payload: { cart, removedItem: insuranceItemId },
      });
    }

    await dispatch(handleReturnsInsuranceAttributes());

    if (response?.userErrors?.length > 0) {
      throw new Error(response.userErrors[0]?.message);
    }

    await dispatch({
      type: ACTIONS.ADD_LINE_ITEMS_SUCCESS,
      payload: { cart, addedItems: items, properties, origination },
      ...trackingInfo,
    });
  } catch (error) {
    Log.error(error, 'Adding line item failed.');
    cart = await CartAPI.fetchCart();
    await dispatch({
      type: ACTIONS.ADD_LINE_ITEMS_FAILURE,
      payload: { cart, addedItems: items, error: defaultErrorMessage, properties },
      ...trackingInfo,
    });
  }

  if (!wasCartOpen && Settings.cartUpsellsV2Enabled) {
    await dispatch(fetchCartUpsells(cart));
  }

  if (!wasCartOpen && Settings.cartAcceleratedPaymentsEnabled) {
    await dispatch(fetchPaymentSettings());
  }
};

export const updateLineItem = (variantId, quantity) => async (dispatch, getState) => {
  const state = getState();
  const currentCart = get(state, ['ajaxCart', 'data']);
  const attributes = get(currentCart, ['entities', 'item', variantId, 'attributes']);

  dispatch({ type: ACTIONS.UPDATE_LINE_ITEM_REQUEST, payload: { variantId, quantity } });

  try {
    let { cart } = await CartAPI.updateLineItem(variantId, quantity, attributes);

    // If removing item (quantity = 0), check if we need to remove insurance
    if (quantity === 0) {
      const { cart: updatedCart, removedInsuranceId } = await removeInsuranceIfLastItem(cart);
      cart = updatedCart;

      if (removedInsuranceId) {
        dispatch({
          type: ACTIONS.UPDATE_LINE_ITEM_SUCCESS,
          payload: { cart, updatedItem: variantId },
        });
        dispatch({
          type: ACTIONS.REMOVE_LINE_ITEM_SUCCESS,
          payload: { cart, removedItem: removedInsuranceId },
        });
        return;
      }
    }

    // Check eligibility after update
    await dispatch(handleReturnsInsuranceAttributes());

    dispatch({
      type: ACTIONS.UPDATE_LINE_ITEM_SUCCESS,
      payload: { cart, updatedItem: variantId },
    });
  } catch (error) {
    Log.error(error, 'Updating line item failed.');
    const cart = await CartAPI.fetchCart();
    dispatch({
      type: ACTIONS.UPDATE_LINE_ITEM_FAILURE,
      payload: { cart, error: defaultErrorMessage, updatedItem: variantId },
    });
  }
};

export const removeLineItem = variantId => async dispatch => {
  dispatch({ type: ACTIONS.REMOVE_LINE_ITEM_REQUEST, payload: { variantId } });
  try {
    let { cart } = await CartAPI.removeLineItem(variantId);
    const { cart: updatedCart } = await removeInsuranceIfLastItem(cart);

    await dispatch(handleReturnsInsuranceAttributes());
    dispatch({
      type: ACTIONS.REMOVE_LINE_ITEM_SUCCESS,
      payload: { cart: updatedCart, removedItem: variantId },
    });
  } catch (error) {
    Log.error(error, 'Removing line item failed.');
    const cart = await CartAPI.fetchCart();
    dispatch({
      type: ACTIONS.REMOVE_LINE_ITEM_FAILURE,
      payload: { cart, error: defaultErrorMessage, removedItem: variantId },
    });
  }
};

export const setGiftingOptions =
  ({ message, logoFreePackaging }) =>
  async dispatch => {
    dispatch({ type: ACTIONS.SET_GIFTING_OPTIONS, payload: { message, logoFreePackaging } });
    try {
      await CartAPI.setLogoFreePackaging(logoFreePackaging);
      await CartAPI.setGiftMessage(message);
      const cart = await CartAPI.fetchCart();
      dispatch({ type: ACTIONS.SET_GIFTING_OPTIONS_SUCCESS, payload: { cart } });
    } catch (error) {
      Log.error(error, 'Setting logo free packaging failed.');
      const cart = await CartAPI.fetchCart();
      dispatch({
        type: ACTIONS.SET_GIFTING_OPTIONS_FAILURE,
        payload: { cart, error: defaultErrorMessage },
      });
    }
  };

export const clearCart = () => async dispatch => {
  dispatch({ type: ACTIONS.CLEAR_CART_REQUEST });
  try {
    await CartAPI.clearCart();
    const cart = await CartAPI.fetchCart();
    dispatch({ type: ACTIONS.CLEAR_CART_SUCCESS, payload: { cart } });
  } catch (error) {
    Log.error(error, 'Clearing cart failed.');
    const cart = await CartAPI.fetchCart();
    dispatch({
      type: ACTIONS.CLEAR_CART_FAILURE,
      payload: { cart, error: defaultErrorMessage },
    });
  }
};

/* eslint-enable no-await-in-loop */
/* eslint-enable no-restricted-syntax */

export const handleShoprunnerCart = () => async dispatch => {
  if (Settings.shoprunnerEnabled) {
    const srToken = window?.shopping?.analytics?.tracker?.shopper?.authToken;
    const isShoprunnerLoggedIn = !!srToken;
    dispatch({
      type: ACTIONS.UPDATE_CART_ATTRIBUTES_REQUEST,
      payload: {
        srToken,
        isShoprunnerLoggedIn,
      },
    });
    try {
      await CartAPI.setAttributes({
        _isLoggedInToShopRunner: `${isShoprunnerLoggedIn}`,
        _srToken: isShoprunnerLoggedIn ? srToken : 'unset',
      });
      dispatch({
        type: ACTIONS.UPDATE_CART_ATTRIBUTES_SUCCESS,
        payload: {
          srToken,
          isShoprunnerLoggedIn,
        },
      });
    } catch (error) {
      console.error('Error adding ShopRunner attributes to cart:', error);
      dispatch({
        type: ACTIONS.UPDATE_CART_ATTRIBUTES_FAILURE,
        payload: {
          srToken,
          isShoprunnerLoggedIn,
          error,
        },
      });
    }
  }
};

export const handleReturnsInsuranceExposure =
  (isExposed = false, analyticsData) =>
  async dispatch => {
    dispatch({
      type: ACTIONS.SET_RETURNS_INSURANCE_RENDERED,
      payload: isExposed,
    });

    if (isExposed) {
      Rudderstack.experimentViewed({
        analyticsData,
        experimentType: 'exposure',
      });
    }
  };

export const addDiscounts = discountCodes => async dispatch => {
  dispatch({ type: ACTIONS.ADD_DISCOUNT_REQUEST });
  try {
    const { cart } = await CartAPI.addDiscounts(discountCodes);
    dispatch({ type: ACTIONS.ADD_DISCOUNT_SUCCCESS, payload: { cart } });
  } catch (error) {
    Log.error(error, 'Adding discount failed.');
    dispatch({ type: ACTIONS.ADD_DISCOUNT_FAILURE, payload: { error } });
  }
};

export const trackCartReconstructionEvent = numCartItemsToAdd => async dispatch => {
  dispatch({
    type: ACTIONS.TRACK_EVENT,
    eventCategory: 'CartReconstruction',
    eventAction: 'Triggered Cart Reconstruction',
    eventLabel: 'triggered_cart_reconstruction',
    eventValue: numCartItemsToAdd,
  });
};
